// Sign in: an offer to keep your streak, not a gate. Google or an emailed link — no passwords.
import { useState, type FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router'
import { Tile } from '../components/game-ui.tsx'
import { Icon } from '../components/Icon.tsx'
import { useToast } from '../components/shell.tsx'
import { ApiFailure, api } from '../lib/api.ts'
import { useAuthConfig, useMe } from '../lib/data.ts'

export default function SignInPage() {
  const me = useMe().data
  const config = useAuthConfig().data
  const [params] = useSearchParams()
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const toast = useToast()
  const error = params.get('error')
  const streak = me?.streak ?? 0

  async function send(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    try {
      await api.post('/auth/email', { email: email.trim() })
      setSent(email.trim())
    } catch (err) {
      toast(err instanceof ApiFailure && err.body.error === 'invalid_email' ? 'That doesn’t look like an email address' : err instanceof ApiFailure && err.body.error === 'rate_limited' ? 'Too many links asked for — try again later' : 'Couldn’t send the link. Try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="page signin">
      <div className="topbar"><Link className="ibtn" to="/you" aria-label="Back"><Icon name="back" /></Link><div className="title" /></div>
      <div className="signin-body">
        {streak > 0 && <div className="streak-tiles">{[...String(streak)].map((d, i) => <Tile key={i} l={d} k={`played${i % 2 ? ' gold' : ''}`} size={46} pt={false} />)}</div>}
        <h1 className="h-serif">{streak > 0 ? <>Keep your<br /><i>{streak}-day streak.</i></> : <>Save your<br /><i>games.</i></>}</h1>
        <p className="muted">{me?.kind === 'member' ? `You're signed in as ${me.email ?? me.displayName}.` : <>You’ve been playing as <b className="ink">{me?.displayName ?? 'a guest'}</b> on this device. Sign in and your games come with you.</>}</p>

        {error && <div className="signin-error">{error === 'expired' ? 'That sign-in link expired or was already used. Ask for a new one below.' : 'Google sign-in didn’t finish. Try again, or use an email link.'}</div>}

        <div className="perks">
          <div className="perk"><span className="ic"><Icon name="streak" /></span><div><b>Your streak on every device</b><span>Phone, laptop, tablet — one history.</span></div></div>
          <div className="perk"><span className="ic"><Icon name="trophy" /></span><div><b>Your name on the leaderboard</b><span>Pick a name, or keep {me?.displayName ?? 'yours'}.</span></div></div>
          <div className="perk"><span className="ic"><Icon name="message" /></span><div><b>Join the discussion</b><span>Talk strategy once you’ve finished an edition.</span></div></div>
        </div>

        {sent ? (
          <div className="card sent"><Icon name="mail" size={22} /><div><b>Check your email.</b><p className="muted small">We sent a sign-in link to {sent}. It works for 15 minutes.</p></div></div>
        ) : (
          <div className="signin-actions">
            {config?.google && <a className="btn ink block" href="/api/v1/auth/google"><GoogleG />Continue with Google</a>}
            {config?.google && <div className="or faint"><span className="rule" /><span className="mono small">OR</span><span className="rule" /></div>}
            <form onSubmit={send} className="email-form">
              <input type="email" required autoComplete="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} aria-label="Email address" />
              <button className={`btn ${config?.google ? 'ghost' : 'ink'} block`} type="submit" disabled={busy || !email.trim()}><Icon name="mail" />Email me a sign-in link</button>
            </form>
          </div>
        )}
        <p className="faint small center">No passwords. Guests can keep playing without an account. <a href="/privacy">Privacy</a></p>
      </div>
    </div>
  )
}

function GoogleG() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C36.9 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
    </svg>
  )
}
